import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { influencerStats, InfluencerStat } from "@/mocks/dropship";
import StarAuthModal, { StarUser } from "./components/StarAuthModal";
import StarDashboard from "./components/StarDashboard";
import { useSEO, SITE_URL } from "@/hooks/useSEO";

type PlatformFilter = "All" | "YouTube" | "TikTok" | "Facebook" | "Twitter/X";

const platformColors: Record<string, string> = {
  YouTube: "bg-red-100 text-red-600",
  TikTok: "bg-stone-100 text-stone-700",
  Facebook: "bg-blue-100 text-blue-600",
  "Twitter/X": "bg-sky-100 text-sky-600",
};

export default function SocialStarsPage() {
  useSEO({
    title: "Social Stars | Influencer Commission Program | Crown Global",
    description: "Join Crown Global's Social Stars program. Promote factory products on YouTube, TikTok, Facebook, and Twitter. Earn up to 22% commission on every sale. Real-time tracking and weekly payouts.",
    keywords: "influencer program, social media commission, TikTok affiliate, YouTube affiliate, earn commission, dropship influencer",
    canonical: "/social-stars",
    ogTitle: "Social Stars Influencer Program — Crown Global",
    ogDescription: "Promote factory products on YouTube, TikTok, Facebook, Twitter. Earn up to 22% commission per sale.",
    ogImage: "https://public.readdy.ai/ai/img_res/68959fee1ce5ad771da112cb0bce41e3.jpg",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/social-stars`,
          "url": `${SITE_URL}/social-stars`,
          "name": "Social Stars Influencer Program — Crown Global",
          "description": "Join Crown Global's influencer commission program. Promote factory products on social media and earn up to 22% commission on every sale.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Social Stars", "item": `${SITE_URL}/social-stars` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/social-stars#service`,
          "url": `${SITE_URL}/social-stars`,
          "name": "Crown Global Social Stars Influencer Program",
          "description": "Earn up to 22% commission promoting factory-direct products on YouTube, TikTok, Facebook, and Twitter/X.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": "Worldwide",
          "serviceType": "Influencer Affiliate Program",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free to join. Earn 10–22% commission on every sale."
          }
        }
      ]
    }
  });

  const [filter, setFilter] = useState<PlatformFilter>("All");
  const [selected, setSelected] = useState<InfluencerStat | null>(null);
  const [authModal, setAuthModal] = useState<"login" | "signup" | null>(null);
  const [currentUser, setCurrentUser] = useState<StarUser | null>(null);

  const filtered = filter === "All" ? influencerStats : influencerStats.filter(s => s.platform === filter);

  const totalSold = influencerStats.reduce((a, s) => a + s.productsSold, 0);
  const totalCommission = influencerStats.reduce((a, s) => a + s.commissionEarned, 0);
  const totalRevenue = influencerStats.reduce((a, s) => a + s.totalRevenue, 0);

  const handleAuthSuccess = (user: StarUser) => {
    setCurrentUser(user);
    setAuthModal(null);
  };

  const handleUpdateUser = (updated: StarUser) => {
    setCurrentUser(updated);
  };

  // If logged in, show dashboard
  if (currentUser) {
    return (
      <>
        <Navbar />
        <div className="pt-20">
          <StarDashboard
            user={currentUser}
            onLogout={() => setCurrentUser(null)}
            onUpdateUser={handleUpdateUser}
          />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

          {/* Hero */}
          <div className="relative rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=social%20media%20influencer%20content%20creator%20studio%20setup%20ring%20light%20camera%20professional%20photography%20modern%20colorful%20background%20YouTube%20TikTok%20Facebook%20Twitter%20vibrant&width=1400&height=340&seq=social1&orientation=landscape"
              alt="Social Stars"
              className="w-full h-56 md:h-72 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16">
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-3">
                Facebook · TikTok · Twitter · YouTube Stars
              </h1>
              <p className="text-white/80 text-base max-w-xl mb-6">
                Join as an influencer, promote dropship products on your channel, and earn real commissions on every sale.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setAuthModal("signup")}
                  className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-add-line mr-2"></i>Join as a Star
                </button>
                <button
                  onClick={() => setAuthModal("login")}
                  className="bg-white/20 hover:bg-white/30 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border border-white/40"
                >
                  <i className="ri-login-box-line mr-2"></i>Star Login
                </button>
              </div>
            </div>
          </div>

          {/* Global stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Total Products Sold", value: totalSold.toLocaleString(), icon: "ri-shopping-bag-3-line", color: "bg-amber-50 text-amber-500" },
              { label: "Total Revenue Generated", value: "$" + (totalRevenue / 1000).toFixed(0) + "K", icon: "ri-money-dollar-circle-line", color: "bg-green-50 text-green-500" },
              { label: "Total Commission Paid", value: "$" + (totalCommission / 1000).toFixed(0) + "K", icon: "ri-gift-line", color: "bg-rose-50 text-rose-500" },
              { label: "Active Influencers", value: influencerStats.length.toString(), icon: "ri-user-star-line", color: "bg-orange-50 text-orange-500" },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl p-5 flex items-center gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${s.color}`}>
                  <i className={`${s.icon} text-2xl`}></i>
                </div>
                <div>
                  <div className="text-xl font-extrabold text-gray-900">{s.value}</div>
                  <div className="text-xs text-gray-400">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Platform filter */}
          <div className="flex flex-wrap gap-3 mb-8">
            {(["All", "YouTube", "TikTok", "Facebook", "Twitter/X"] as PlatformFilter[]).map(p => (
              <button
                key={p}
                onClick={() => setFilter(p)}
                className={`flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${filter === p ? "bg-amber-400 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-500"}`}
              >
                {p === "YouTube" && <i className="ri-youtube-line"></i>}
                {p === "TikTok" && <i className="ri-tiktok-line"></i>}
                {p === "Facebook" && <i className="ri-facebook-circle-line"></i>}
                {p === "Twitter/X" && <i className="ri-twitter-x-line"></i>}
                {p === "All" && <i className="ri-global-line"></i>}
                {p}
              </button>
            ))}
          </div>

          {/* Influencer cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {filtered.map(star => (
              <div
                key={star.id}
                className="bg-white rounded-xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
                onClick={() => setSelected(star)}
              >
                <div className="relative w-full h-52 overflow-hidden">
                  <img
                    src={star.image}
                    alt={star.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute top-3 left-3 flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${platformColors[star.platform] || "bg-gray-100 text-gray-600"}`}>
                    <i className={star.platform_icon}></i> {star.platform}
                  </div>
                  {star.verified && (
                    <div className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center bg-amber-400 rounded-full">
                      <i className="ri-shield-check-fill text-white text-sm"></i>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <div className="flex items-center justify-between text-white">
                      <div>
                        <div className="text-xs text-white/70">Commission Earned</div>
                        <div className="text-lg font-extrabold text-amber-300">${star.commissionEarned.toLocaleString()}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/70">Products Sold</div>
                        <div className="text-lg font-extrabold text-white">{star.productsSold.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-bold text-gray-800 text-base">{star.name}</h3>
                    <span className="text-sm">{star.flag}</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">{star.country} · {star.followers} followers</p>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-amber-50 rounded-lg p-2 text-center">
                      <div className="text-sm font-extrabold text-amber-500">{star.commissionRate}%</div>
                      <div className="text-xs text-gray-400">Rate</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <div className="text-sm font-extrabold text-green-600">${(star.totalRevenue / 1000).toFixed(0)}K</div>
                      <div className="text-xs text-gray-400">Revenue</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <div className="text-sm font-extrabold text-gray-700">{star.productsSold.toLocaleString()}</div>
                      <div className="text-xs text-gray-400">Sold</div>
                    </div>
                  </div>

                  <button
                    onClick={e => { e.stopPropagation(); setSelected(star); }}
                    className="w-full bg-amber-400 hover:bg-amber-500 text-white font-semibold py-2.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    View Commission Details
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Influencer detail modal */}
          {selected && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
              <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="relative">
                  <div className="w-full h-48 overflow-hidden rounded-t-2xl">
                    <img src={selected.image} alt={selected.name} className="w-full h-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent rounded-t-2xl"></div>
                  </div>
                  <button onClick={() => setSelected(null)} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full cursor-pointer transition-colors">
                    <i className="ri-close-line text-lg"></i>
                  </button>
                  <div className="absolute bottom-4 left-5 text-white">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${platformColors[selected.platform] || "bg-gray-100 text-gray-600"}`}>
                        <i className={`${selected.platform_icon} mr-1`}></i>{selected.platform}
                      </span>
                      {selected.verified && <span className="flex items-center gap-1 text-xs text-amber-300 font-semibold"><i className="ri-shield-check-fill"></i> Verified</span>}
                    </div>
                    <h2 className="text-2xl font-extrabold">{selected.name}</h2>
                    <p className="text-white/70 text-sm">{selected.flag} {selected.country} · {selected.followers} followers</p>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {[
                      { label: "Commission Rate", value: `${selected.commissionRate}%`, color: "bg-amber-50 text-amber-600" },
                      { label: "Total Earned", value: `$${selected.commissionEarned.toLocaleString()}`, color: "bg-green-50 text-green-600" },
                      { label: "Products Sold", value: selected.productsSold.toLocaleString(), color: "bg-sky-50 text-sky-600" },
                      { label: "Revenue Driven", value: `$${(selected.totalRevenue / 1000).toFixed(0)}K`, color: "bg-rose-50 text-rose-600" },
                    ].map(s => (
                      <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`}>
                        <div className="text-xl font-extrabold">{s.value}</div>
                        <div className="text-xs opacity-70 mt-0.5">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-amber-50 rounded-xl p-4 mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-400 rounded-full shrink-0">
                      <i className="ri-trophy-fill text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-amber-600 font-semibold">Top Selling Product</p>
                      <p className="text-sm font-bold text-gray-800">{selected.topProduct}</p>
                    </div>
                  </div>

                  <h3 className="font-bold text-gray-800 mb-3">Recent Sales &amp; Commissions</h3>
                  <div className="flex flex-col gap-2 mb-6">
                    {selected.recentSales.map((sale, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{sale.product}</p>
                          <p className="text-xs text-gray-400">{sale.qty} units · {sale.date}</p>
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          <div className="text-sm font-bold text-green-600">+${sale.commission.toFixed(2)}</div>
                          <div className="text-xs text-gray-400">commission</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => { setSelected(null); setAuthModal("signup"); }}
                    className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Join &amp; Start Earning Like {selected.name.split(" ")[0]}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* How to join */}
          <div className="bg-white rounded-2xl p-8 md:p-12">
            <div className="text-center mb-10">
              <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-2">Earn While You Create</p>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">Join as a Social Media Star</h2>
              <p className="text-gray-500 text-sm max-w-lg mx-auto">Sign up, pick products, share on your channel, and earn up to 22% commission on every sale you drive.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
              {[
                { icon: "ri-user-add-line", title: "1. Sign Up Free", desc: "Create your influencer account and connect your social media platform." },
                { icon: "ri-store-2-line", title: "2. Pick Products", desc: "Browse our dropship catalog and choose products that match your audience." },
                { icon: "ri-share-line", title: "3. Share Videos", desc: "Post videos on YouTube, TikTok, Facebook, or Twitter with your referral link." },
                { icon: "ri-money-dollar-circle-line", title: "4. Earn Commission", desc: "Earn up to 22% commission on every sale — tracked in real-time, paid weekly." },
              ].map(s => (
                <div key={s.title} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-2xl mx-auto mb-4">
                    <i className={`${s.icon} text-amber-500 text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => setAuthModal("signup")}
                className="bg-amber-400 hover:bg-amber-500 text-white font-bold px-10 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-2"></i>Sign Up as a Star
              </button>
              <button
                onClick={() => setAuthModal("login")}
                className="border-2 border-amber-400 text-amber-500 hover:bg-amber-50 font-bold px-10 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-login-box-line mr-2"></i>Already a Star? Login
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Auth modal */}
      {authModal && (
        <StarAuthModal
          mode={authModal}
          onClose={() => setAuthModal(null)}
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
}
